import axios from 'axios';
import { addBreadcrumb, flush } from '../utils/diagnostics/breadcrumbs';

/**
 * SHER-215 — every localStorage key the sign-in flow writes (or that the app
 * later derives from the user's session). Single source of truth so signOut,
 * the Dashboard 401 fallback, and any future "force re-auth" code paths can't
 * drift out of sync and leave User A's state visible to User B.
 *
 * Adding a new key the sign-in flow writes? Add it here too.
 */
export const SESSION_LOCAL_STORAGE_KEYS = [
    'jwt',
    'password',
    'rawKeyResearcher',
    'keyResearcher',           // legacy alias still written by src/actions/index.js
    'type',
    'forceChangePassword',
    'name',
    'surnames',
    'uuid',
    'email',
    'uuidInvestigation',
    'rawKeyInvestigation',
    // SHER-215 (review follow-up) — sync bookkeeping written by patientsActions /
    // utils/index.jsx during a session. Leaving these means User B's incremental
    // sync would resume from User A's last event ID / last-update timestamp.
    'data_download',
    'data_download_date',
    'lastUpdatePatients',
    'patient_sync_state',
    'lastSyncTimestamp',
    'lastSyncedPatientCount',
    'lastEventId',
] as const;

/**
 * SHER-215 (review follow-up) — these prefixes match keys whose suffix is a
 * dynamic uuid (per-investigation). They have to be cleared by sweep, not by
 * exact match, because the SPA writes one entry per investigation the user has
 * touched in this session.
 *
 * `rawKeyInvestigation_<uuid>` is the most security-sensitive: it holds the
 * *decrypted* investigation AES key. Leaving these across signOut violates the
 * zero-knowledge invariant called out in CLAUDE.md and lets a subsequent user
 * on the same browser read User A's investigation keys directly from
 * localStorage.
 */
export const SESSION_LOCAL_STORAGE_KEY_PREFIXES = [
    'rawKeyInvestigation_',
    'lastEventId_',
] as const;

/**
 * SHER-209 — who tore this session down.
 *
 * `rawKeyResearcher` has exactly one writer (authService.js:188) and six
 * clearers, and the CHOM-Dakar report is that it disappears mid-session while
 * the JWT survives. Which clearer runs there is not derivable from the code: the
 * Storage-prototype instrumentation added after the ATTADIR report does capture
 * a stack, but production is minified and the frames it caught
 * (`rJi -> 379:10312 -> h:379:8307`, authReducer.js:23-31) were never resolved.
 *
 * A minified frame cannot be made readable from here — but it does not need to
 * be. Every teardown path is a named call site, so naming it at the call turns
 * an unresolvable stack into an enum that survives minification.
 *
 * Add a value here when you add a caller. `unspecified` is not a placeholder to
 * settle for: it means the next report will again fail to say who did it.
 */
export type SessionTeardownReason =
    | 'sign-out'        // deliberate: the user signed out (or an explicit expel did)
    | 'dashboard-401'   // the boot investigations probe came back 401
    | 'signin-mount'    // the sign-in page mounted (SHER-216 defensive wipe)
    | 'unspecified';

/**
 * SHER-215 — fully detach the SPA from the previous user's session.
 *
 * Pedro's repro: log out User A → log in User B → background paginations from
 * User A keep firing with User A's JWT, returning 403 + (Pedro reports)
 * missing CORS headers, surfacing as "CORS error" in the console. Root cause
 * was that signOut only cleared 5 localStorage keys and never reset axios's
 * default Authorization header, so every NEW request fired before signIn
 * (or fired in parallel with signIn) carried the stale JWT.
 *
 * This helper is the single place where we tear the session down. It does
 * NOT clear IndexedDB / patient cache — that's a separate concern owned by
 * `clearPatientsStore()` in src/db, called by the signOut redux action.
 *
 * SHER-209 — pass a `reason`. It is recorded, with whether the session actually
 * held a key, so a mid-session wipe is attributable without resolving a minified
 * stack. See SessionTeardownReason above.
 */
export function clearSession(reason: SessionTeardownReason = 'unspecified'): void {
    // Record what this teardown destroyed BEFORE destroying it. `hadRawKey` is
    // the field that matters: a teardown of a session that held no key is
    // routine, a teardown of one that did is the reported bug.
    //
    // Deliberately best-effort and fully swallowed — diagnostics must never stop
    // a sign-out from completing, because a half-torn-down session is a
    // cross-user data leak (SHER-215).
    try {
        const hadRawKey = Boolean(localStorage.getItem('rawKeyResearcher'));
        const hadJwt = Boolean(localStorage.getItem('jwt'));
        addBreadcrumb('state', 'session.clearSession', {
            errorName: reason,
            reason,
            hadRawKey,
            hadJwt,
            route: typeof window !== 'undefined' ? window.location?.pathname : undefined,
        });
        // The signature of SHER-209: a session that still looks alive (JWT and
        // key both present) is wiped by something that is not a sign-out. Flush
        // now — the breadcrumb ring buffer is 100 events deep, and by the time
        // the user reports "patient creation fails" it has long rolled over.
        if (hadRawKey && hadJwt && reason !== 'sign-out') {
            flush('session-wipe-mid-session');
        }
    } catch (_) { /* never let diagnostics block a teardown */ }
    for (const key of SESSION_LOCAL_STORAGE_KEYS) {
        try { localStorage.removeItem(key); } catch (_) { /* SSR / locked storage */ }
    }
    // Sweep dynamic-suffix keys (per-investigation entries — see prefix list above).
    // Snapshot keys() first because removeItem during iteration mutates the set.
    try {
        const allKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const k = localStorage.key(i);
            if (k) allKeys.push(k);
        }
        for (const k of allKeys) {
            if (SESSION_LOCAL_STORAGE_KEY_PREFIXES.some((p) => k.startsWith(p))) {
                try { localStorage.removeItem(k); } catch (_) { /* ignore */ }
            }
        }
    } catch (_) { /* never break sign-out on a localStorage hiccup */ }
    // Reset axios's Authorization default. Without this, requests started
    // *after* signOut but *before* the next signIn (route-guard validate,
    // background sync, etc.) keep using the previous user's JWT.
    try { delete (axios.defaults.headers as any).Authorization; } catch (_) { /* ignore */ }
    try { delete (axios.defaults.headers as any).authorization; } catch (_) { /* ignore */ }
    try {
        if (axios.defaults.headers.common) {
            delete (axios.defaults.headers.common as any).Authorization;
            delete (axios.defaults.headers.common as any).authorization;
        }
    } catch (_) { /* ignore */ }
}
